import { useCallback } from 'react';
import useSWRMutation from 'swr/mutation';
import { useRevalidateUserFollowStatus } from '@app/_hooks/use-fetch-user-follow-status';
import { followUser, unfollowUser } from '@app/_repositories/mutation-service';

const KEY = 'follow-user';

type FollowUserParams = {
  userId: string;
  mutualFollowSeriesId?: string;
};

type UnFollowUserParams = {
  userId: string;
};

export const useFollow = () => {
  const { revalidateUserFollowStatus } = useRevalidateUserFollowStatus();
  const { trigger, isMutating, error } = useSWRMutation<
    any,
    any,
    typeof KEY,
    FollowUserParams
  >(
    KEY,
    (
      _,
      {
        arg: { userId, mutualFollowSeriesId },
      }: {
        arg: FollowUserParams;
      }
    ) => {
      return followUser(userId, mutualFollowSeriesId);
    }
  );

  const update = useCallback(
    async ({ userId, mutualFollowSeriesId }: FollowUserParams) => {
      return revalidateUserFollowStatus(
        userId,
        trigger({ userId, mutualFollowSeriesId }).then(() => true),
        {
          optimisticData: true,
          rollbackOnError: true,
        }
      );
    },
    [revalidateUserFollowStatus, trigger]
  );

  return {
    update,
    isMutating,
    error,
  };
};

export const useUnfollow = () => {
  const { revalidateUserFollowStatus } = useRevalidateUserFollowStatus();
  const { trigger, isMutating, error } = useSWRMutation<
    any,
    any,
    typeof KEY,
    UnFollowUserParams
  >(
    KEY,
    (
      _,
      {
        arg: { userId },
      }: {
        arg: UnFollowUserParams;
      }
    ) => {
      return unfollowUser(userId);
    }
  );

  const update = useCallback(
    async ({ userId }: UnFollowUserParams) => {
      return revalidateUserFollowStatus(
        userId,
        trigger({ userId }).then(() => false),
        {
          optimisticData: false,
          rollbackOnError: true,
        }
      );
    },
    [revalidateUserFollowStatus, trigger]
  );

  return {
    update,
    isMutating,
    error,
  };
};
